<template>
  <div class="fn-list">
    <!-- Header -->
    <div class="fn-header">
      <div class="fn-header__left">
        <h2 class="fn-header__title">Functions</h2>
        <span class="fn-header__count" v-if="allItems">{{ allItems.length }} / 150</span>
      </div>
      <div class="fn-header__right">
        <div class="fn-search">
          <i class="fa fa-search fn-search__icon"></i>
          <input
            type="text"
            class="fn-search__input"
            v-model="searchQuery"
            :placeholder="$t('searchByNameOrTags')"
          />
        </div>
        <button
          class="fn-btn fn-btn--create"
          @click="createModal = true"
          :disabled="allItems && allItems.length >= 150"
        >
          <i class="fa fa-plus"></i>
          <span>New Function</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div class="fn-grid" v-if="!allItems">
      <div class="fn-card fn-card--skeleton" v-for="n in 4" :key="n">
        <div class="fn-card__body">
          <div class="skel skel--title"></div>
          <div class="skel skel--line"></div>
          <div class="skel skel--line skel--short"></div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div class="fn-empty" v-else-if="items.length === 0 && !searchQuery">
      <div class="fn-empty__icon">
        <i class="fa fa-code"></i>
      </div>
      <p class="fn-empty__text">{{ $t('seemsLikeThereIsNothingHere') }}</p>
      <button class="fn-btn fn-btn--create" @click="createModal = true">
        <i class="fa fa-plus"></i>
        <span>Create your first function</span>
      </button>
    </div>

    <!-- Grid -->
    <template v-else>
      <div class="fn-grid" v-if="items.length > 0">
        <div
          class="fn-card"
          v-for="item in items"
          :key="item.uuid"
          @click="$router.push(`/admin/functions/${item.uuid}`)"
        >
          <div class="fn-card__header">
            <div class="fn-card__icon">
              <i class="fa fa-code"></i>
            </div>
          </div>

          <div class="fn-card__body">
            <h3 class="fn-card__name">{{ item.name }}</h3>

            <div class="fn-card__versions">
              <div class="fn-card__version">
                <span class="fn-card__version-label">Dev</span>
                <span
                  class="fn-card__version-value"
                  :class="{ 'is-active': item.dev_version }"
                >{{ item.dev_version || 'inactive' }}</span>
              </div>
              <div class="fn-card__version">
                <span class="fn-card__version-label">Live</span>
                <span
                  class="fn-card__version-value"
                  :class="{ 'is-live': item.public_version }"
                >{{ item.public_version || 'unpublished' }}</span>
              </div>
            </div>
          </div>

          <div class="fn-card__footer" v-if="item.tags && item.tags.length > 0">
            <span class="fn-card__tag" v-for="tag in item.tags" :key="tag">{{ tag }}</span>
          </div>
        </div>
      </div>

      <div class="fn-no-results" v-else>
        <p>No functions matching "{{ searchQuery }}"</p>
      </div>
    </template>

    <!-- Create modal -->
    <main-dialog
      :show="createModal"
      title="New Function"
      subtitle="Create a new function"
      max-width="440px"
      @close="createModal = false"
    >
      <div class="fn-form">
        <div class="fn-field">
          <label class="fn-label">{{ $t('name') }}</label>
          <protokol-invalid-feedback :errors="validator.fieldErrors('name')">
            <input
              type="text"
              class="fn-input"
              v-model="name"
              placeholder="Enter function name…"
              @keydown.enter.prevent="save"
              ref="nameInput"
            />
          </protokol-invalid-feedback>
        </div>
      </div>

      <template #footer>
        <button class="fn-btn fn-btn--cancel" @click="createModal = false">Cancel</button>
        <button
          class="fn-btn fn-btn--create"
          @click="save"
          :disabled="!model.name || model.name.trim().length < 3"
        >
          <i class="fa fa-plus"></i>
          <span>Create</span>
        </button>
      </template>
    </main-dialog>
  </div>
</template>

<script>
import MainDialog from "@/components/MainDialog.vue";

export default {
  name: "FunctionList",
  components: { MainDialog },
  data() {
    return {
      allItems: null,
      searchQuery: "",
      createModal: false,
      validator: this.$validator(),
      model: {
        name: "",
      },
    };
  },
  computed: {
    items() {
      if (!this.allItems) return [];
      if (!this.searchQuery || !this.searchQuery.trim()) return this.allItems;
      const query = this.searchQuery.toLowerCase().trim();
      return this.allItems.filter((item) => {
        if (item.name && item.name.toLowerCase().includes(query)) return true;
        if (item.tags && Array.isArray(item.tags)) {
          return item.tags.some((tag) => tag.toLowerCase().includes(query));
        }
        return false;
      });
    },
    name: {
      get() {
        return this.model.name.replaceAll(" ", "_").replaceAll(/[0-9]/g, "");
      },
      set(val) {
        this.model.name = val.replaceAll(" ", "_").replaceAll(/[0-9]/g, "");
      },
    },
  },
  watch: {
    model: {
      handler() {
        this.validator.revalidate();
      },
      deep: true,
    },
    createModal(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.$refs.nameInput) this.$refs.nameInput.focus();
        });
      }
    },
  },
  methods: {
    save() {
      if (!this.validator.pass()) return;

      let popup = this.$loadingPopup();
      this.$functions
        .create(this.model)
        .then((resp) => {
          this.createModal = false;
          this.$router.push(`/admin/functions/${resp.data.uuid}`);
        })
        .catch((err) => {
          console.error(err);
          this.$bvToast.toast(this.$t("unexpectedError"), {
            title: this.$t("error"),
            variant: "danger",
          });
        })
        .finally(() => {
          popup.hide();
        });
    },
  },
  created() {
    this.validator.validate(this.model, {
      isValidDNSName: (model, errors) => {
        if (model.name.length < 1) {
          errors.add("name", [this.$t("requiredField")]);
          return;
        }
        if (model.name.length < 3) {
          errors.add("name", [this.$t("notValid")]);
        }
      },
    });
  },
  mounted() {
    if (!this.$can("manage base_functions")) {
      return this.$router.back();
    }

    this.$functions
      .all()
      .then((resp) => {
        this.allItems = resp.data;
      })
      .catch((err) => {
        this.$debugLog(err);
        this.$pageUnavailable();
      });
  },
};
</script>

<style scoped lang="scss">
.fn-list {
  padding: 8px 0;
}

.fn-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.fn-header__left {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.fn-header__right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.fn-header__title {
  font-size: 22px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.fn-header__count {
  font-size: 13px;
  color: var(--text-muted);
  font-weight: 500;
}

// ─── Search ────────────────────────────────────
.fn-search {
  position: relative;
  display: flex;
  align-items: center;
}

.fn-search__icon {
  position: absolute;
  left: 12px;
  font-size: 12px;
  color: var(--text-muted);
  pointer-events: none;
}

.fn-search__input {
  width: 220px;
  height: 38px;
  padding: 0 14px 0 34px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }
  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

// ─── Buttons ───────────────────────────────────
.fn-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  outline: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.18s ease;

  &--create {
    padding: 9px 20px;
    background: var(--primary-start);
    color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);

    &:hover {
      opacity: 0.88;
      transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
    }

    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }

  &--cancel {
    padding: 8px 18px;
    background: transparent;
    border: 1px solid var(--border-hover, #4e5058);
    color: var(--text-secondary);

    &:hover {
      background: var(--bg-card-secondary);
      color: var(--text-primary);
    }
  }
}

// ─── Grid ──────────────────────────────────────
.fn-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

// ─── Card ──────────────────────────────────────
.fn-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &:hover {
    border-color: rgba(108, 128, 255, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  }
}

.fn-card__header {
  padding: 16px 18px 0;
}

.fn-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(168, 85, 247, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a855f7;
  font-size: 15px;
}

.fn-card__body {
  padding: 14px 18px 16px;
  flex: 1;
}

.fn-card__name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 14px;
  line-height: 1.3;
  word-break: break-word;
}

.fn-card__versions {
  display: flex;
  gap: 16px;
}

.fn-card__version {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.fn-card__version-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--text-muted);
}

.fn-card__version-value {
  font-size: 13px;
  color: var(--text-secondary);
  font-family: 'SFMono-Regular', 'Consolas', monospace;

  &.is-active {
    color: #57d9a3;
  }

  &.is-live {
    color: var(--primary-start);
  }
}

.fn-card__footer {
  padding: 0 18px 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.fn-card__tag {
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-secondary);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

// ─── Empty / no results ────────────────────────
.fn-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.fn-empty__icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(168, 85, 247, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: var(--text-muted);
  margin-bottom: 16px;
}

.fn-empty__text {
  font-size: 15px;
  color: var(--text-muted);
  margin: 0 0 20px;
}

.fn-no-results {
  padding: 40px 20px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
}

// ─── Form (modal) ──────────────────────────────
.fn-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fn-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fn-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0;
}

.fn-input {
  width: 100%;
  height: 42px;
  padding: 0 14px;
  background: var(--form-bg, #383a40);
  border: 1px solid var(--form-border, #4e5058);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder { color: var(--text-muted); }
  &:focus {
    border-color: var(--primary-start);
    box-shadow: 0 0 0 3px rgba(91, 124, 246, 0.12);
  }
}

// ─── Skeleton ──────────────────────────────────
.fn-card--skeleton {
  pointer-events: none;
  padding: 18px;

  .skel {
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.06);
    animation: skel-pulse 1.4s ease-in-out infinite;

    &--title {
      width: 60%;
      height: 18px;
      margin-bottom: 14px;
    }

    &--line {
      width: 80%;
      height: 12px;
      margin-bottom: 10px;
    }

    &--short {
      width: 45%;
    }
  }
}

@keyframes skel-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 0.8; }
}
</style>
